import React, {useEffect} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useForm, useField} from 'react-final-form-hooks';
import {OptionsType} from '@pluto/mrss-defaults/MrssDefaultsTypes';
import {getFilmLabels} from '@pluto/get-film-labels';
import {
    Descriptor,
    MetadataTerritorialLanguage,
    FormFieldType,
} from './ContentItemViewMetadataTypes';
import {getTextWithEmptyFieldError} from './ContentItemViewMetadataUtils';

const onSubmit = () => {};

export interface MetadataTerritorialLanguageProps {
    rating: string;
    countryCode: string;
    captionRequired: string;
    ratingDescriptors: string[];
    enableRatingDescriptors: Descriptor[];
    cancelEditMode: boolean;
}

export type ChangeEvent = {
    target: {
        checked: boolean;
    };
};

export const handleRatingChange = curry(
    (countryCode: string, onChange: (val: string) => void, {target: {value}}: any) => {
        onChange(value);
    }
);

export const mapDescriptorValues = (descriptor: Descriptor) => descriptor.code;
export const filterFieldValues = curry(
    (countryCodeDescriptorValues: string[], value: string) =>
        !countryCodeDescriptorValues.includes(value)
);
export const filterDescriptors = curry(
    (countryCode: string, descriptor: Descriptor) =>
        descriptor.countryCode === countryCode
);

export const getRating = (
    onChange: (val: string) => void,
    value: string,
    rating: string,
    requiredFields: string[]
) => {
    if (!rating) {
        return getTextWithEmptyFieldError(requiredFields, rating, 'rating');
    }

    return rating;
};

export const handleCheckboxChange = curry(
    (
        countryCode: string,
        onChange: (val: string) => void,
        {target: {checked}}: ChangeEvent
    ) => {
        onChange(checked ? 'Yes' : 'No');
    }
);

export const mapDescriptors = (label: string) => ({
    label,
    value: label,
});
export const mapDescriptorOptions = ({label, code}: Descriptor) => ({
    label,
    value: code,
});

export const getDescriptorLabels = (ratingDescriptors: string[], className: string) => {
    const withLabels = ratingDescriptors.map(mapDescriptors);
    return getFilmLabels(withLabels, className);
};

export const mapValues = (val: OptionsType) => val.value || val;

export const filterChosenValues = (values: string[]) => {
    if (!values.length) {
        return [];
    }
    const validValues: string[] = values.map(mapValues as any);
    // find and remove from array selected value
    const [chosenValue] = validValues.splice(validValues.length - 1, 1);

    if (!validValues.includes(chosenValue)) {
        return [...validValues, chosenValue];
    }

    validValues.splice(validValues.indexOf(chosenValue), 1);

    return validValues;
};

export const handleDescriptorChange = curry(
    (
        countryCode: string,
        onChange: (val: string[]) => void,
        event: React.ChangeEvent<any>,
        values: string[]
    ) => {
        const newValues = filterChosenValues(values);

        onChange(newValues);
    }
);

export const checkEveryElement = curry((descriptorValues: string[], value: string) => {
    return descriptorValues.includes(value);
});

export const getDescriptorOptions = (
    descriptorsByCountryCode: Descriptor[],
    fieldValue: string[],
    existAllElements: boolean
) => {
    if (!existAllElements) {
        const countryCodeDescriptorValues = descriptorsByCountryCode.map(
            mapDescriptorValues
        );
        const notValidValues = fieldValue.filter(
            filterFieldValues(countryCodeDescriptorValues)
        );
        return [
            ...descriptorsByCountryCode.map(mapDescriptorOptions as any),
            ...notValidValues.map(mapDescriptors),
        ];
    }
    return descriptorsByCountryCode.map(mapDescriptorOptions as any);
};

export const filterValidDescriptors = curry(
    (ratingDescriptors: string[], {value}: OptionsType) =>
        ratingDescriptors.includes(value)
);
export const filterNotValidDescriptors = curry(
    (filteredDescriptorLabels: string[], label: string) =>
        !filteredDescriptorLabels.includes(label)
);
export const mapLabels = ({label}: OptionsType) => label;

export const getDescriptorsByCountryCode = (
    descriptors: Descriptor[],
    countryCode: string
) => descriptors.filter(filterDescriptors(countryCode));

export const matchElements = (notValidValues: string[], selectedElements: any) => {
    for (const element of selectedElements) {
        const errorClassList = 'error-label__border-label';
        const text = element.textContent;
        const parent = element.parentElement;

        if (notValidValues.includes(text) && parent) {
            parent.classList.add(errorClassList);
        }

        if (parent.classList.contains(errorClassList) && !notValidValues.includes(text)) {
            parent.classList.remove(errorClassList);
        }
    }
};

export const getRatingDescriptors = (
    values: string[],
    onChange: (val: string[]) => void,
    ratingDescriptors: string[],
    countryCode: string,
    enableRatingDescriptors: Descriptor[],
    descriptorsByCountryCode: Descriptor[]
) => {
    const filteredDescriptorLabels = descriptorsByCountryCode.map(mapDescriptorValues);
    const isValidDescriptors = ratingDescriptors.every(
        checkEveryElement(filteredDescriptorLabels)
    );

    const notValidValues = ratingDescriptors.filter(
        filterNotValidDescriptors(filteredDescriptorLabels)
    );
    const filteredDescriptorOptions = descriptorsByCountryCode.map(mapDescriptorOptions);
    const validValues = filteredDescriptorOptions
        .filter(filterValidDescriptors(ratingDescriptors))
        .map(mapLabels);

    if (!isValidDescriptors) {
        const validLabels = getDescriptorLabels(validValues, 'keywords-label');
        const notValidLabels = getDescriptorLabels(
            notValidValues,
            'keywords-label error-label'
        );

        return validLabels?.concat((notValidLabels as unknown) as JSX.Element[]);
    }

    return getDescriptorLabels(validValues, 'keywords-label');
};

export const getValidField = curry(
    (descriptorsByCountryCode: Descriptor[], values: string[]) => {
        const filteredDescriptorLabels = descriptorsByCountryCode.map(
            mapDescriptorValues
        );
        const isValid = values.every(checkEveryElement(filteredDescriptorLabels));
        return isValid ? '' : 'Invalid options, select new';
    }
);

export const setDefaultValues = wrap(
    (
        cancelEditMode: boolean,
        ratingField: FormFieldType,
        captionRequiredField: FormFieldType,
        ratingDescriptorsField: FormFieldType,
        initialValues: MetadataTerritorialLanguage
    ) => {
        if (cancelEditMode) {
            const {rating, captionRequired, ratingDescriptors} = initialValues;
            ratingField.input.onChange(rating);
            captionRequiredField.input.onChange(captionRequired);
            ratingDescriptorsField.input.onChange(ratingDescriptors);
        }
    }
);

export function QCMetadataTerritorialTab(props: MetadataTerritorialLanguageProps) {
    const {
        rating,
        captionRequired,
        ratingDescriptors,
        countryCode,
        enableRatingDescriptors,
        cancelEditMode,
    } = props;

    const requiredFields = ['rating'];
    const initialValues = {
        rating,
        captionRequired,
        ratingDescriptors,
    };

    const descriptorsByCountryCode = getDescriptorsByCountryCode(
        enableRatingDescriptors,
        countryCode
    );
    const {form} = useForm({
        onSubmit,
        initialValues,
    });

    const ratingField = useField('rating', form);
    const captionRequiredField = useField('captionRequired', form);
    const ratingDescriptorsField = useField(
        'ratingDescriptors',
        form,
        getValidField(descriptorsByCountryCode) as any
    );

    useEffect(
        setDefaultValues(
            cancelEditMode,
            ratingField,
            captionRequiredField,
            ratingDescriptorsField,
            initialValues
        ),
        [cancelEditMode]
    );

    return (
        <div className="qc-metadata__territorial-tab-panel">
            <div className="qc-metadata__rating-caption-wrapper">
                <div className="caption-wrapper">
                    <div className="caption-label">Captions Required</div>
                    <div className="caption-value">{captionRequired}</div>
                </div>
                <div className="rating-wrapper">
                    <div className="rating-label">Rating*</div>
                    <div className="rating-value">
                        {getRating(
                            ratingField.input.onChange,
                            ratingField.input.value,
                            rating,
                            requiredFields
                        )}
                    </div>
                </div>
            </div>
            <div className="rating-reason">
                <div className="rating-reason-label">Rating Descriptors</div>
                {getRatingDescriptors(
                    ratingDescriptorsField.input.value || [],
                    ratingDescriptorsField.input.onChange,
                    ratingDescriptors,
                    countryCode,
                    enableRatingDescriptors,
                    descriptorsByCountryCode
                )}
            </div>
        </div>
    );
}

export default QCMetadataTerritorialTab;
